/**
 * Export utilities for FarmDash data
 */

// Convert data to CSV format
const convertToCSV = (data, headers) => {
  if (!data || data.length === 0) return '';
  
  const headerRow = headers.join(',');
  const rows = data.map(row => 
    headers.map(header => {
      const value = row[header.key] ?? '';
      // Escape commas and quotes in values
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  return [headerRow.replace(/,/g, () => ',')].concat(rows).join('\n');
};

// Download file helper
const downloadFile = (content, filename, type = 'text/csv') => {
  const blob = new Blob([content], { type: `${type};charset=utf-8;` });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Get current date for filename
const getDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Export weekly activity data
 */
export const exportActivityData = (weeklyData) => {
  const entries = [];
  
  Object.entries(weeklyData?.weeks || {}).forEach(([week, weekEntries]) => {
    weekEntries.forEach(entry => {
      entries.push({
        week,
        project: entry.project,
        volume: entry.volume || 0,
        cost: entry.cost || 0,
        points: entry.points || 0,
        pnl: (entry.volume || 0) - (entry.cost || 0),
        date: entry.date || ''
      });
    });
  });
  
  // Sort by week descending
  entries.sort((a, b) => b.week.localeCompare(a.week));
  
  const headers = [
    { key: 'week', label: 'Week' },
    { key: 'project', label: 'Project' },
    { key: 'volume', label: 'Volume ($)' },
    { key: 'cost', label: 'Costs ($)' },
    { key: 'pnl', label: 'PNL ($)' },
    { key: 'points', label: 'Points' },
    { key: 'date', label: 'Date' }
  ];
  
  const headerLabels = headers.map(h => h.label).join(',');
  const rows = entries.map(entry => 
    headers.map(h => {
      const value = entry[h.key] ?? '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  const csv = [headerLabels, ...rows].join('\n');
  downloadFile(csv, `farmdash-activity-${getDateString()}.csv`);
  
  return entries.length;
};

/**
 * Export projects/airdrops configuration
 */
export const exportProjectsData = (projectSettings) => {
  const projects = Object.entries(projectSettings).map(([name, settings]) => ({
    name,
    type: settings.type || 'Trading',
    capital: settings.capital || 0,
    tge: settings.tge || 'TBD',
    status: settings.status || 'Active',
    priority: settings.priority || 'Medium',
    weeklyPoints: settings.weeklyPoints || 0,
    weeklyVolume: settings.weeklyVolume || 0,
    roiBearish: settings.roiScenarios?.bearish || 0,
    roiBase: settings.roiScenarios?.base || 0,
    roiBullish: settings.roiScenarios?.bullish || 0,
    startDate: settings.startDate || '',
    notes: settings.notes || ''
  }));
  
  const headers = [
    { key: 'name', label: 'Project Name' },
    { key: 'type', label: 'Type' },
    { key: 'capital', label: 'Capital ($)' },
    { key: 'tge', label: 'TGE' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
    { key: 'weeklyPoints', label: 'Weekly Points Target' },
    { key: 'weeklyVolume', label: 'Weekly Volume Target' },
    { key: 'roiBearish', label: 'ROI Bearish ($/pt)' },
    { key: 'roiBase', label: 'ROI Base ($/pt)' },
    { key: 'roiBullish', label: 'ROI Bullish ($/pt)' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'notes', label: 'Notes' }
  ];
  
  const headerLabels = headers.map(h => h.label).join(',');
  const rows = projects.map(project => 
    headers.map(h => {
      const value = project[h.key] ?? '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  const csv = [headerLabels, ...rows].join('\n');
  downloadFile(csv, `farmdash-projects-${getDateString()}.csv`);
  
  return projects.length;
};

/**
 * Export received airdrops
 */
export const exportReceivedAirdrops = (receivedAirdrops) => {
  const airdrops = receivedAirdrops.map(airdrop => ({
    project: airdrop.project,
    tokens: airdrop.tokens || 0,
    totalSold: airdrop.totalSold || 0,
    salesCount: airdrop.sales?.length || 0,
    dateReceived: airdrop.dateReceived || ''
  }));
  
  const headers = [
    { key: 'project', label: 'Project' },
    { key: 'tokens', label: 'Tokens Received' },
    { key: 'totalSold', label: 'Total Sold ($)' },
    { key: 'salesCount', label: 'Number of Sales' },
    { key: 'dateReceived', label: 'Date Received' }
  ];
  
  const headerLabels = headers.map(h => h.label).join(',');
  const rows = airdrops.map(airdrop => 
    headers.map(h => {
      const value = airdrop[h.key] ?? '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  const csv = [headerLabels, ...rows].join('\n');
  downloadFile(csv, `farmdash-airdrops-received-${getDateString()}.csv`);
  
  return airdrops.length;
};

/**
 * Export complete summary report
 */
export const exportSummaryReport = (weeklyData, projectSettings, receivedAirdrops, stats, roiCalculation) => {
  const lines = [];
  const date = new Date().toLocaleDateString();
  
  // Header
  lines.push('FARMDASH SUMMARY REPORT');
  lines.push(`Generated: ${date}`);
  lines.push('');
  
  // Overall Stats
  lines.push('=== OVERALL STATISTICS ===');
  lines.push(`Total Volume,$${stats?.totalVolume || 0}`);
  lines.push(`Total Costs,$${stats?.totalCosts || 0}`);
  lines.push(`Total Points,${stats?.totalPoints || 0}`);
  lines.push(`Cost per Point,$${stats?.totalPoints > 0 ? (stats.totalCosts / stats.totalPoints).toFixed(4) : 0}`);
  lines.push('');
  
  // ROI Projections
  lines.push('=== ROI PROJECTIONS ===');
  lines.push(`Bearish,$${roiCalculation?.bearish || 0}`);
  lines.push(`Base,$${roiCalculation?.base || 0}`);
  lines.push(`Bullish,$${roiCalculation?.bullish || 0}`);
  lines.push('');
  
  // Projects Summary
  lines.push('=== PROJECTS SUMMARY ===');
  lines.push('Project,Type,Capital,Status,TGE');
  Object.entries(projectSettings).forEach(([name, settings]) => {
    lines.push(`${name},${settings.type || 'Trading'},$${settings.capital || 0},${settings.status || 'Active'},${settings.tge || 'TBD'}`);
  });
  lines.push('');
  
  // Received Airdrops
  if (receivedAirdrops && receivedAirdrops.length > 0) {
    lines.push('=== AIRDROPS RECEIVED ===');
    lines.push('Project,Tokens,Total Sold');
    receivedAirdrops.forEach(airdrop => {
      lines.push(`${airdrop.project},${airdrop.tokens},$${airdrop.totalSold || 0}`);
    });
    lines.push('');
  }
  
  // Total Received
  const totalReceived = receivedAirdrops?.reduce((sum, a) => sum + (a.totalSold || 0), 0) || 0;
  lines.push('=== TOTALS ===');
  lines.push(`Total Invested (Capital),$${Object.values(projectSettings).reduce((sum, p) => sum + (p.capital || 0), 0)}`);
  lines.push(`Total Costs (Fees),$${stats?.totalCosts || 0}`);
  lines.push(`Total Received from Airdrops,$${totalReceived}`);
  lines.push(`Projected ROI (Base),$${roiCalculation?.base || 0}`);
  
  const csv = lines.join('\n');
  downloadFile(csv, `farmdash-summary-${getDateString()}.csv`);
  
  return true;
};